import { useStore } from 'hooks/useStore';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Paginator } from 'primereact/paginator';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  AuditTrailEntry,
  bidAnalysisServices,
  BidDetails,
} from 'services/bidAnalysis/bidAnalysisServices';
import {
  calculateGPPerUnit,
  calculateGrossProfit,
} from '../BidAnalysisTemplate/bidAnalysisFormulas';
import classes from './bidValidationTemplate.module.scss';

interface ValidationResult {
  rule_name: string;
  status: 'warning' | 'success';
  text: string;
}

interface ValidationGroup {
  rule_name: string;
  items: ValidationResult[];
  hasWarnings: boolean;
  hasSuccess: boolean;
  warningCount: number;
  successCount: number;
}

const BidValidationTemplate = observer(() => {
  const navigate = useNavigate();
  const { bidId } = useParams<{ bidId: string }>();
  const { BidAnalysisStore, UserStore } = useStore();

  const [_validationResults, setValidationResults] = useState<ValidationResult[]>([]);
  const [validationGroups, setValidationGroups] = useState<ValidationGroup[]>([]);
  const [loading, setLoading] = useState(true);

  // Get bidDetails from store instead of making API call
  // Use optional chaining and provide fallback to ensure it's always defined
  const bidDetails = BidAnalysisStore?.currentBidDetails || null;
  const [selectedReviewer, setSelectedReviewer] = useState<string | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [_searchQuery, _setSearchQuery] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<ValidationGroup | null>(null);
  const [showIssuesModal, setShowIssuesModal] = useState(false);
  const [auditTrail, setAuditTrail] = useState<AuditTrailEntry[]>([]);
  const [auditTrailLoading, setAuditTrailLoading] = useState(false);
  // Pagination state for audit trail
  const [auditTrailCurrentPage, setAuditTrailCurrentPage] = useState(0);
  const [auditTrailTotalRecords, setAuditTrailTotalRecords] = useState(0);
  const [auditTrailTotalPages, setAuditTrailTotalPages] = useState(0);
  const [auditTrailPageSize] = useState(10); // 10 items per page for audit trail
  const [users, setUsers] = useState<
    Array<{
      label: string;
      value: string;
      username: string;
      email: string;
      attributes: string;
    }>
  >([]);
  const [loadingEligibleUsers, setLoadingEligibleUsers] = useState(false);

  useEffect(() => {
    if (bidId) {
      loadValidationData();
      loadAuditTrail(0); // Load first page on initial mount
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidId]);

  // Load eligible users after bidDetails is available
  useEffect(() => {
    if (bidDetails) {
      loadEligibleUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidDetails]);

  const calculateAggregatedMetrics = (bidDetails: BidDetails) => {
    const isNC = bidDetails.template_type === 'nc';

    // For NC template: calculate average across all years (option_year 1-5)
    if (isNC) {
      const yearMetrics: Array<{ gpPerUnit: number; profitPercentage: number }> = [];

      const formData = bidDetails.form_details?.form_data;
      if (formData && Array.isArray(formData)) {
        formData.forEach(concept => {
          if (concept.pack_size) {
            Object.values(concept.pack_size).forEach((packData: any) => {
              // For NC, pack_size[packSizeKey] is an array of year entries
              // Each entry has option_year (1-5) representing Base Year + 4 Option Years
              if (Array.isArray(packData)) {
                packData.forEach((yearEntry: any) => {
                  // Use backend field names (snake_case)
                  const revenue = parseFloat(String(yearEntry.contract_annual_revenue || '0')) || 0;
                  const gp = parseFloat(String(yearEntry.contract_annual_gp || '0')) || 0;
                  const annualUsage = parseFloat(String(yearEntry.nc_est_annual_usage || '0')) || 0;

                  // Try to use gp_per_unit and gross_profit from backend if available, otherwise calculate
                  const gpPerUnit =
                    yearEntry.gp_per_unit != null
                      ? parseFloat(String(yearEntry.gp_per_unit))
                      : calculateGPPerUnit(gp, annualUsage);

                  const profitPercentage =
                    yearEntry.gross_profit != null
                      ? parseFloat(String(yearEntry.gross_profit))
                      : calculateGrossProfit(revenue, gp);

                  // Only add if we have valid data (non-zero values)
                  if (revenue > 0 || gp > 0 || annualUsage > 0) {
                    yearMetrics.push({
                      gpPerUnit: isNaN(gpPerUnit) ? 0 : gpPerUnit,
                      profitPercentage: isNaN(profitPercentage) ? 0 : profitPercentage,
                    });
                  }
                });
              }
            });
          }
        });
      }

      // Calculate averages across all years - always divide by 5 for NC (Base Year + 4 Option Years)
      if (yearMetrics.length > 0) {
        const avgGpPerUnit = yearMetrics.reduce((sum, m) => sum + m.gpPerUnit, 0) / 5;
        const avgProfitPercentage = yearMetrics.reduce((sum, m) => sum + m.profitPercentage, 0) / 5;

        return {
          totalRevenue: 0, // Not used for NC
          totalGP: 0, // Not used for NC
          totalTargetUsage: 0, // Not used for NC
          profitPercentage: avgProfitPercentage,
          gpPerUnit: avgGpPerUnit,
        };
      }

      // Fallback if no data
      return {
        totalRevenue: 0,
        totalGP: 0,
        totalTargetUsage: 0,
        profitPercentage: 0,
        gpPerUnit: 0,
      };
    }

    // For MAS template: use existing logic (sum across all pack sizes)
    let totalRevenue = 0;
    let totalGP = 0;
    let totalTargetUsage = 0;

    const formData = bidDetails.form_details?.form_data;
    if (formData && Array.isArray(formData)) {
      formData.forEach(concept => {
        if (concept.pack_size) {
          Object.values(concept.pack_size).forEach((packData: any) => {
            // Sum contract_annual_revenue
            if (packData.contract_annual_revenue) {
              totalRevenue += parseFloat(String(packData.contract_annual_revenue));
            }
            // Sum contract_annual_gp
            if (packData.contract_annual_gp) {
              totalGP += parseFloat(String(packData.contract_annual_gp));
            }
            // Sum target_annual_usage
            if (packData.target_annual_usage) {
              totalTargetUsage += parseFloat(String(packData.target_annual_usage));
            }
          });
        }
      });
    }

    return {
      totalRevenue,
      totalGP,
      totalTargetUsage,
      profitPercentage: calculateGrossProfit(totalRevenue, totalGP),
      gpPerUnit: calculateGPPerUnit(totalGP, totalTargetUsage),
    };
  };

  const loadEligibleUsers = async () => {
    if (!bidDetails) {
      toast.error('Bid details not available');
      return;
    }

    setLoadingEligibleUsers(true);
    try {
      // Step 1: Determine contract type
      let contractType = '';
      if (bidDetails.template_type === 'nc') {
        contractType = 'NC';
      } else if (bidDetails.template_type === 'mas') {
        if (bidDetails.product_type === 'direct') {
          contractType = 'MAS direct';
        } else if (bidDetails.product_type === 'indirect') {
          contractType = 'MAS indirect';
        }
      }

      if (!contractType) {
        toast.error('Unable to determine contract type');
        setUsers([]);
        return;
      }

      // Step 2: Calculate aggregated metrics
      const metrics = calculateAggregatedMetrics(bidDetails);

      // Handle edge cases - send null if values are 0 or invalid
      // Round to 2 decimal places for consistency
      const gpPerUnit = metrics.gpPerUnit > 0 ? Math.round(metrics.gpPerUnit * 100) / 100 : null;
      const profitPercentage =
        !isNaN(metrics.profitPercentage) && isFinite(metrics.profitPercentage)
          ? Math.round(metrics.profitPercentage * 100) / 100
          : null;

      // Step 3: Call assignment rules API
      const response = await bidAnalysisServices.getEligibleUsers({
        contract_type: contractType,
        gpPerUnit,
        profitPercentage,
      });

      // Step 4: Map eligible users to dropdown format with attributes
      const eligibleUserOptions = response.eligible_users.map(user => {
        // Get attribute names
        const attributeNames = user.attributes.map(attr => attr.attribute_name).join(', ');
        // Handle cases where username might be undefined or empty
        const displayName =
          user.username && user.username.trim() !== '' ? user.username : user.user_id;
        // Format: username (user_id) [attribute_name]
        const label = `${displayName} (${user.user_id}) [${attributeNames}]`;

        return {
          label,
          value: user.user_id,
          username: displayName,
          email: user.user_id,
          attributes: attributeNames,
        };
      });

      // Step 5: Filter out the current analyst from eligible reviewers
      // Get current user email from UserStore
      const currentUserEmail = UserStore?.userMetadata?.userInfo?.email || '';
      const analystEmail = bidDetails?.analyst || '';

      // Filter out users where user_id matches current analyst email
      // Compare case-insensitively to handle email variations
      const filteredOptions = eligibleUserOptions.filter(user => {
        const userEmailLower = (user.email || user.value || '').toLowerCase().trim();
        const currentEmailLower = currentUserEmail.toLowerCase().trim();
        const analystEmailLower = analystEmail.toLowerCase().trim();

        // Exclude if user matches current user email or analyst email
        return userEmailLower !== currentEmailLower && userEmailLower !== analystEmailLower;
      });

      setUsers(filteredOptions);

      if (filteredOptions.length === 0) {
        toast.warn('No eligible reviewers found based on assignment rules', {
          autoClose: 5000,
        });
      }
    } catch (error: any) {
      console.error('Error loading eligible users:', error);
      const errorMsg = error?.response?.data?.message || 'Failed to load eligible reviewers';
      toast.error(errorMsg, { autoClose: 5000 });
      setUsers([]); // Empty list on error
    } finally {
      setLoadingEligibleUsers(false);
    }
  };

  const loadValidationData = async () => {
    if (!bidId) return;

    setLoading(true);
    try {
      // Load bid details if not already in store (e.g., when navigating directly to this page)
      if (
        !BidAnalysisStore?.currentBidDetails ||
        BidAnalysisStore.currentBidDetails.bid_master_id !== Number(bidId)
      ) {
        await BidAnalysisStore.fetchBidDetails(Number(bidId));
      }

      // Only call validateBid - getBidDetails is not needed here
      // If bid details are needed, they should be passed from the previous page or loaded separately
      const validations = await bidAnalysisServices.validateBid(Number(bidId));

      setValidationResults(validations);

      // Group validation results by rule_name
      const grouped: Record<string, ValidationResult[]> = {};
      validations.forEach(result => {
        if (!grouped[result.rule_name]) {
          grouped[result.rule_name] = [];
        }
        grouped[result.rule_name].push(result);
      });

      // Convert to array and create groups
      const groups: ValidationGroup[] = Object.entries(grouped).map(([rule_name, items]) => {
        const warningItems = items.filter(item => item.status === 'warning');
        const successItems = items.filter(item => item.status === 'success');
        return {
          rule_name,
          items,
          hasWarnings: warningItems.length > 0,
          hasSuccess: successItems.length > 0,
          warningCount: warningItems.length,
          successCount: successItems.length,
        };
      });

      // Sort: groups with warnings first, then by rule name
      groups.sort((a, b) => {
        if (a.hasWarnings && !b.hasWarnings) return -1;
        if (!a.hasWarnings && b.hasWarnings) return 1;
        return a.rule_name.localeCompare(b.rule_name);
      });

      setValidationGroups(groups);
    } catch (error) {
      console.error('Error loading validation data:', error);
      toast.error('Failed to load validation results', { autoClose: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitForApproval = async () => {
    if (users.length === 0) {
      toast.error(
        'No eligible reviewers found. Please configure assignment rules or contact an administrator.',
        { autoClose: 7000 }
      );
      return;
    }

    if (!selectedReviewer) {
      toast.error('Please select a reviewer', { autoClose: 5000 });
      return;
    }

    if (!bidId) {
      toast.error('Bid ID is missing', { autoClose: 5000 });
      return;
    }

    setSubmitting(true);
    try {
      const result = await bidAnalysisServices.sendForReview(Number(bidId), selectedReviewer);

      toast.success(result.message || 'Bid submitted for review successfully', { autoClose: 5000 });
      navigate(`/bdportal/bid-analysis/dashboard`);
    } catch (error: any) {
      console.error('Error submitting for approval:', error);
      const errorMessage =
        error?.response?.data?.message || error?.message || 'Failed to submit for review';
      toast.error(errorMessage, { autoClose: 5000 });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveAsDraft = () => {
    navigate(`/bdportal/bid-analysis/template/${bidId}`);
  };

  const _handleRequestConsultation = () => {
    toast.info('Consultation request feature coming soon', { autoClose: 5000 });
  };

  const _handleViewIssues = (group: ValidationGroup) => {
    setSelectedGroup(group);
    setShowIssuesModal(true);
  };

  const _getGroupTitle = (ruleName: string): string => {
    // Map rule names to user-friendly titles
    const titleMap: Record<string, string> = {
      'Required Fields should be filled': 'All required fields completed',
      'NDC Validation': 'NDC codes validated',
      'Formula validation': 'Formula validation verified',
    };

    return titleMap[ruleName] || ruleName;
  };

  const _getGroupDescription = (group: ValidationGroup): string => {
    // Use the first item's text as description, or create a summary
    if (group.items.length > 0) {
      const firstItem = group.items[0];
      if (group.items.length === 1) {
        return firstItem.text;
      } else {
        return `${group.items.length} validation ${group.items.length === 1 ? 'issue' : 'issues'} found`;
      }
    }
    return 'No description available';
  };

  const loadAuditTrail = async (page: number = 0) => {
    if (!bidId) return;

    setAuditTrailLoading(true);
    try {
      // Frontend page 0 = Backend page 1
      const backendPage = page + 1;
      const response = await bidAnalysisServices.getAuditTrail(Number(bidId), {
        page: backendPage,
        page_size: auditTrailPageSize,
      });
      setAuditTrail(response.data || []);
      setAuditTrailTotalRecords(response.pagination?.total_count || 0);
      setAuditTrailTotalPages(response.pagination?.total_pages || 0);
    } catch (error) {
      console.error('Error loading audit trail:', error);
      toast.error('Failed to load audit trail', { autoClose: 5000 });
      setAuditTrail([]);
      setAuditTrailTotalRecords(0);
      setAuditTrailTotalPages(0);
    } finally {
      setAuditTrailLoading(false);
    }
  };

  const onAuditTrailPageChange = (event: any) => {
    // PrimeReact Paginator provides first and rows
    const page = Math.floor(event.first / event.rows);
    setAuditTrailCurrentPage(page);
    loadAuditTrail(page);
  };

  const getAuditTrailRowsPerPage = () => {
    return auditTrailPageSize;
  };

  const getAuditTrailFirstRecord = () => {
    return auditTrailCurrentPage * auditTrailPageSize;
  };

  const formatAuditTrailDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      // Format as: YYYY-MM-DD HH:MM
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  const _warningCount = validationGroups.reduce((sum, group) => sum + group.warningCount, 0);
  const _productName = bidDetails?.product_name || 'Loading...';

  // Mock reports data
  const _reports = [
    {
      title: 'Executive Summary',
      description:
        'Concise 2-page overview with key findings, recommendations, and financial projections.',
      pages: 2,
      format: 'PDF',
      readingTime: '~3 minutes',
    },
    {
      title: 'Detailed Analysis',
      description:
        'Comprehensive 15-page report with full market analysis, cost breakdowns, and competitive intelligence.',
      pages: 15,
      format: 'PDF',
      readingTime: '~8 minutes',
    },
    {
      title: 'Cost Breakdown Report',
      description: 'Detailed COGS analysis with formula explanations and sensitivity analysis.',
      pages: 5,
      format: 'Excel/PDF',
      readingTime: '~3 minutes',
    },
    {
      title: 'Market Analysis Deep Dive',
      description:
        'In-depth market intelligence with competitive positioning and opportunity assessment.',
      pages: 8,
      format: 'PDF',
      readingTime: '~5 minutes',
    },
    {
      title: 'Competitive Intelligence Brief',
      description:
        'Competitor analysis with pricing strategies, market positioning, and threat assessment.',
      pages: 6,
      format: 'PDF',
      readingTime: '~4 minutes',
    },
  ];

  if (loading) {
    return (
      <div className={classes.validationWrapper}>
        <div className={classes.loadingContainer}>
          <i className="pi pi-spinner pi-spin" style={{ fontSize: '3rem', color: '#522d6e' }}></i>
          <p>Loading validation results...</p>
        </div>
      </div>
    );
  }

  if (!bidDetails && bidId) {
    return (
      <div className={classes.validationWrapper}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <i
              className="pi pi-exclamation-triangle"
              style={{ fontSize: '3rem', color: '#f59e0b' }}
            ></i>
            <h3 style={{ marginTop: '1rem' }}>Bid Not Found</h3>
            <p style={{ color: '#6b7280' }}>Unable to load bid details for ID: {bidId}</p>
            <Button
              label="Go to Dashboard"
              icon="pi pi-arrow-left"
              onClick={() => navigate('/bdportal/bid-analysis/dashboard')}
              style={{ marginTop: '1rem' }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.validationWrapper}>
      <div className={classes.content}>
        {/* Analysis Validation Section - Commented out per user request */}
        {/* <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <h2 className={classes.sectionTitle}>| Analysis Validation</h2>
          
          </div>
          <div className={classes.validationList}>
            {validationGroups.length === 0 ? (
              <div className={classes.emptyState}>
                <i className="pi pi-info-circle" style={{ fontSize: '2rem', color: '#9ca3af' }}></i>
                <p>No validation results available</p>
              </div>
            ) : (
              validationGroups.map((group, index) => (
                <div key={index} className={classes.validationItem}>
                  <div className={classes.validationItemLeft}>
                    {group.hasWarnings ? (
                      <i className="pi pi-exclamation-triangle" style={{ color: '#f59e0b' }}></i>
                    ) : (
                      <i className="pi pi-check-circle" style={{ color: '#10b981' }}></i>
                    )}
                    <div className={classes.validationItemContent}>
                      <span className={classes.validationItemTitle}>{getGroupTitle(group.rule_name)}</span>
                      <p className={classes.validationItemDescription}>{getGroupDescription(group)}</p>
                    </div>
                  </div>
                  {group.hasWarnings && group.items.length > 0 && (
                    <Button
                      label={`View ${group.warningCount} ${group.warningCount === 1 ? 'Issue' : 'Issues'}`}
                      icon="pi pi-list"
                      outlined
                      size="small"
                      onClick={() => handleViewIssues(group)}
                      className={classes.viewIssuesButton}
                    />
                  )}
                </div>
              ))
            )}
          </div>
          <div className={classes.readyForReview}>
            <i className="pi pi-clock" style={{ fontSize: '3rem', color: '#f59e0b' }}></i>
            <div>
              <h3>Ready for Review</h3>
              <p>
                {warningCount} warning{warningCount !== 1 ? 's' : ''} require attention before final approval.
              </p>
            </div>
          </div>
        </div> */}

        {/* Approval Workflow Section */}
        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <h2 className={classes.sectionTitle}>| Approval Workflow</h2>
          </div>
          <div className={classes.approvalContent}>
            <div className={classes.submitSection}>
              <h3>Submit for Review</h3>
              <div className={classes.formGroup}>
                <label>Reviewer *</label>
                <Dropdown
                  value={selectedReviewer}
                  options={[{ label: 'Select reviewer', value: null }, ...users]}
                  onChange={e => setSelectedReviewer(e.value)}
                  placeholder={
                    loadingEligibleUsers ? 'Loading eligible reviewers...' : 'Select reviewer'
                  }
                  className={classes.dropdown}
                  optionLabel="label"
                  optionValue="value"
                  loading={loadingEligibleUsers}
                  disabled={loadingEligibleUsers || users.length === 0}
                  filter
                  filterBy="label"
                  filterPlaceholder="Search by name, email, or attribute..."
                  showClear
                />
                <small style={{ color: '#6b7280', display: 'block', marginTop: '0.25rem' }}>
                  Eligible reviewers based on assignment rules
                </small>
              </div>
              <div className={classes.formGroup}>
                <label>Review Notes</label>
                <InputTextarea
                  value={reviewNotes}
                  onChange={e => setReviewNotes(e.target.value)}
                  placeholder="Add any specific notes or areas of focus for the reviewer..."
                  rows={5}
                  className={classes.textarea}
                />
              </div>
            </div>
          </div>
          <div className={classes.actionButtons}>
            <Button
              label="Back To Workspace"
              icon="pi pi-arrow-left"
              outlined
              onClick={handleSaveAsDraft}
              className={classes.actionBtn}
            />
            <Button
              label="Submit For Approval"
              icon="pi pi-send"
              onClick={handleSubmitForApproval}
              loading={submitting}
              disabled={submitting || loadingEligibleUsers || users.length === 0}
              className={classes.actionBtnPrimary}
            />
          </div>
        </div>

        {/* Reports & Export Section - Commented out per user request */}
        {/* <div className={classes.section} style={{ position: 'relative' }}>
          <div className={classes.sectionHeader}>
            <h2 className={classes.sectionTitle}>| Reports & Export</h2>
          </div>
          <div className={classes.reportsGrid}>
            {reports.map((report, index) => (
              <div key={index} className={classes.reportCard}>
                <h4>{report.title}</h4>
                <p>{report.description}</p>
                <div className={classes.reportMeta}>
                  <span>{report.pages} pages</span>
                  <span>•</span>
                  <span>{report.format}</span>
                  <span>•</span>
                  <span>{report.readingTime}</span>
                </div>
              </div>
            ))}
          </div>
          <div className={classes.exportButtons}>
            <Button
              icon="pi pi-file-pdf"
              label="Export as PDF"
              outlined
              className={classes.exportBtn}
            />
            <Button
              icon="pi pi-file-excel"
              label="Export to Excel"
              outlined
              className={classes.exportBtn}
            />
            <Button
              icon="pi pi-file"
              label="Export to Powerpoint"
              outlined
              className={classes.exportBtn}
            />
            <Button
              icon="pi pi-envelope"
              label="Email Package"
              outlined
              className={classes.exportBtn}
            />
          </div>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px',
            zIndex: 10
          }}>
            <div style={{
              backgroundColor: '#5b5fd6',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
            }}>
              Coming Soon
            </div>
          </div>
        </div> */}

        {/* Audit Trail Section */}
        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <h2 className={classes.sectionTitle}>| Audit Trail</h2>
            <div className={classes.auditLinks}>
              <a href="#" className={classes.auditLink}>
                Export Log
              </a>
            </div>
          </div>
          <div className={classes.auditTrail}>
            {auditTrailLoading ? (
              <div className={classes.emptyState}>
                <i
                  className="pi pi-spinner pi-spin"
                  style={{ fontSize: '1.5rem', color: '#522d6e' }}
                ></i>
                <p>Loading audit trail...</p>
              </div>
            ) : auditTrail.length === 0 ? (
              <div className={classes.emptyState}>
                <i className="pi pi-info-circle" style={{ fontSize: '2rem', color: '#9ca3af' }}></i>
                <p>No audit trail entries available</p>
              </div>
            ) : (
              <>
                {auditTrail.map(entry => (
                  <div key={entry.id} className={classes.auditEntry}>
                    <span className={classes.auditTimestamp}>
                      {formatAuditTrailDate(entry.performed_at)}:
                    </span>
                    <span className={classes.auditAction}>
                      {entry.description || entry.event_type}
                    </span>
                    <span className={classes.auditUser}>by {entry.performed_by}</span>
                  </div>
                ))}
                {/* Pagination - Show if there are multiple pages */}
                {!auditTrailLoading && auditTrailTotalPages > 1 && (
                  <div className={classes.paginationContainer}>
                    <Paginator
                      first={getAuditTrailFirstRecord()}
                      rows={getAuditTrailRowsPerPage()}
                      totalRecords={auditTrailTotalRecords}
                      onPageChange={onAuditTrailPageChange}
                      template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                      pageLinkSize={5}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Issues Modal */}
      <Dialog
        header={
          selectedGroup ? `Validation Issues: ${selectedGroup.rule_name}` : 'Validation Issues'
        }
        visible={showIssuesModal}
        onHide={() => {
          setShowIssuesModal(false);
          setSelectedGroup(null);
        }}
        className={classes.issuesModal}
        style={{ width: '90vw', maxWidth: '800px' }}
        modal
      >
        {selectedGroup && (
          <div className={classes.modalContent}>
            <div className={classes.modalHeader}>
              <div className={classes.modalStats}>
                <span className={classes.statItem}>
                  <i className="pi pi-exclamation-triangle" style={{ color: '#f59e0b' }}></i>
                  {selectedGroup.warningCount} Warning{selectedGroup.warningCount !== 1 ? 's' : ''}
                </span>
                {selectedGroup.successCount > 0 && (
                  <span className={classes.statItem}>
                    <i className="pi pi-check-circle" style={{ color: '#10b981' }}></i>
                    {selectedGroup.successCount} Success
                    {selectedGroup.successCount !== 1 ? 'es' : ''}
                  </span>
                )}
              </div>
            </div>
            <div className={classes.modalList}>
              {selectedGroup.items.map((item, index) => (
                <div
                  key={index}
                  className={`${classes.modalItem} ${
                    item.status === 'warning' ? classes.modalWarningItem : classes.modalSuccessItem
                  }`}
                >
                  <div className={classes.modalItemIcon}>
                    {item.status === 'warning' ? (
                      <i className="pi pi-exclamation-triangle" style={{ color: '#f59e0b' }}></i>
                    ) : (
                      <i className="pi pi-check-circle" style={{ color: '#10b981' }}></i>
                    )}
                  </div>
                  <div className={classes.modalItemContent}>
                    <div className={classes.modalItemHeader}>
                      <span
                        className={`${classes.modalItemStatus} ${
                          item.status === 'warning'
                            ? classes.modalWarningStatus
                            : classes.modalSuccessStatus
                        }`}
                      >
                        {item.status.toUpperCase()}
                      </span>
                    </div>
                    <p className={classes.modalItemText}>{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
});

export default BidValidationTemplate;
